import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { logAdminAction } from "@/admin/services/adminService";
import { useAdminAuth } from "./useAdminAuth";
import type { AdminReportRow, ReportStatus } from "@/admin/types/admin";

export function useReports(statusFilter?: ReportStatus) {
  const queryClient = useQueryClient();
  const { adminUserId } = useAdminAuth();

  const query = useQuery({
    queryKey: ["admin-reports", statusFilter],
    queryFn: async (): Promise<AdminReportRow[]> => {
      let q = supabase
        .from("reports")
        .select("id, report_type, reporter_user_id, target_id, target_type, reason, details, status, resolved_by_admin_id, resolved_at, resolution_note, created_at, updated_at")
        .order("created_at", { ascending: false });

      if (statusFilter) {
        q = q.eq("status", statusFilter);
      }

      const { data, error } = await q;
      if (error) throw error;
      const rows = data ?? [];

      // ── 1. Annuaire utilisateurs via RPC (SECURITY DEFINER — bypass RLS) ────
      // On charge tous les utilisateurs une seule fois pour résoudre :
      //   - les noms des signalants
      //   - les noms des auteurs d'annonces signalées
      //   - les profils des utilisateurs signalés
      const { data: allUsersData } = await supabase.rpc("admin_get_users" as any);
      const allUsers: any[] = allUsersData ?? [];
      const userById: Record<string, any> = {};
      allUsers.forEach((u: any) => { userById[u.user_id] = u; });

      const resolveDisplayName = (userId: string): string => {
        const u = userById[userId];
        if (!u) return "Inconnu";
        return u.display_name || `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || "Inconnu";
      };

      // ── 2. Contenu des annonces signalées ───────────────────────────────────
      const adTargetIds = [...new Set(
        rows.filter((r: any) => r.target_type === "ad").map((r: any) => r.target_id)
      )];
      let adMap: Record<string, any> = {};
      if (adTargetIds.length > 0) {
        const { data: annonces } = await supabase
          .from("annonces")
          .select("id, title, description, wilaya, commune, metier, user_id, created_at")
          .in("id", adTargetIds);

        (annonces ?? []).forEach((a: any) => {
          adMap[a.id] = {
            title: a.title,
            description: a.description ?? null,
            wilaya: a.wilaya,
            commune: a.commune,
            metier: a.metier,
            createdAt: a.created_at,
            authorName: resolveDisplayName(a.user_id),
            authorUserId: a.user_id,
          };
        });
      }

      // ── 3. Profils des utilisateurs signalés ────────────────────────────────
      const userTargetIds = [...new Set(
        rows.filter((r: any) => r.target_type === "user").map((r: any) => r.target_id)
      )];
      let userMap: Record<string, any> = {};
      userTargetIds.forEach((uid) => {
        const u = userById[uid];
        userMap[uid] = {
          displayName: resolveDisplayName(uid),
          userId: uid,
          wilaya: u?.wilaya ?? null,
          specialite: u?.specialite ?? null,
        };
      });

      return rows.map((row: any) => ({
        id: row.id,
        reportType: row.report_type,
        reporterUserId: row.reporter_user_id ?? null,
        targetId: row.target_id,
        targetType: row.target_type,
        reason: row.reason,
        details: row.details ?? null,
        status: row.status as ReportStatus,
        resolvedByAdminId: row.resolved_by_admin_id ?? null,
        resolvedAt: row.resolved_at ?? null,
        resolutionNote: row.resolution_note ?? null,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        reporterName: row.reporter_user_id ? resolveDisplayName(row.reporter_user_id) : null,
        adTarget: row.target_type === "ad" ? (adMap[row.target_id] ?? null) : null,
        userTarget: row.target_type === "user" ? (userMap[row.target_id] ?? null) : null,
      }));
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({
      reportId,
      newStatus,
      reasonCode,
      notes,
    }: {
      reportId: string;
      newStatus: ReportStatus;
      reasonCode?: string;
      notes?: string;
    }) => {
      const now = new Date().toISOString();
      const isResolved = newStatus === "resolved" || newStatus === "dismissed";
      const { error } = await supabase
        .from("reports")
        .update({
          status: newStatus,
          resolved_by_admin_id: isResolved ? adminUserId : null,
          resolved_at: isResolved ? now : null,
          resolution_note: notes ?? null,
          updated_at: now,
        })
        .eq("id", reportId);
      if (error) throw error;
      return { reportId, newStatus, reasonCode, notes };
    },
    onSuccess: async ({ reportId, newStatus, reasonCode, notes }) => {
      if (adminUserId) {
        const actionType =
          newStatus === "resolved"
            ? "report_resolved"
            : newStatus === "dismissed"
            ? "report_dismissed"
            : "report_in_progress";

        await logAdminAction({
          adminUserId,
          actionType,
          targetType: "report",
          targetId: reportId,
          reasonCode,
          notes,
          afterState: { status: newStatus },
        });
      }

      const labels: Record<ReportStatus, string> = {
        open: "Ouvert",
        inProgress: "En cours",
        resolved: "Résolu",
        dismissed: "Classé sans suite",
      };
      toast.success(`Signalement : ${labels[newStatus]}`);
      queryClient.invalidateQueries({ queryKey: ["admin-reports"] });
      queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] });
    },
    onError: () => toast.error("Erreur lors de la mise à jour du signalement"),
  });

  return { query, updateStatusMutation };
}
